import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  XCircle,
  UserCheck,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Edit3,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import { toast } from "sonner";

const ACTION_META = {
  "batch.declined": { icon: XCircle, color: "bg-amber-50 text-amber-700", label: "Declined batch" },
  "batch.assigned": { icon: UserCheck, color: "bg-blue-50 text-blue-700", label: "Assigned batch" },
  "qc.approve": { icon: ThumbsUp, color: "bg-emerald-50 text-emerald-700", label: "Approved" },
  "qc.reject": { icon: ThumbsDown, color: "bg-rose-50 text-rose-700", label: "Rejected" },
  "qc.resend": { icon: RotateCcw, color: "bg-amber-50 text-amber-700", label: "Sent back" },
  "question.edited": { icon: Edit3, color: "bg-slate-100 text-slate-700", label: "Edited" },
  "questions.bulk_delete": { icon: Trash2, color: "bg-rose-50 text-rose-700", label: "Bulk deleted" },
};

const FILTER_ACTIONS = [
  { value: "", label: "All actions" },
  { value: "batch.declined", label: "Declined" },
  { value: "batch.assigned", label: "Assigned" },
  { value: "qc.approve", label: "Approved" },
  { value: "qc.reject", label: "Rejected" },
  { value: "qc.resend", label: "Sent back" },
  { value: "question.edited", label: "Edited" },
];

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const pageSize = 50;
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (actionFilter) params.action = actionFilter;
      const { data } = await api.get("/audit-log", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <PageContainer>
      <PageHeader
        title="Activity Log"
        subtitle="Every decline, assignment, QC action, and edit — newest first"
      />
      <div className="bg-white rounded-xl border border-slate-100 brand-shadow p-3 mb-4 flex items-center gap-2">
        <select
          data-testid="audit-action-filter"
          value={actionFilter}
          onChange={(e) => {
            setPage(1);
            setActionFilter(e.target.value);
          }}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          {FILTER_ACTIONS.map((a) => (
            <option key={a.value || "all"} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <div className="ml-auto text-xs font-mono text-slate-400">{total} events</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 brand-shadow overflow-hidden">
        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-500">No activity yet.</div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {items.map((ev) => (
              <AuditRow key={ev.id} ev={ev} />
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 font-mono">
          <div>
            Page {page} of {pages}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function AuditRow({ ev }) {
  const meta = ACTION_META[ev.action] || {
    icon: Activity,
    color: "bg-slate-100 text-slate-700",
    label: ev.action,
  };
  const Icon = meta.icon;
  const when = ev.at ? new Date(ev.at) : null;
  return (
    <li
      data-testid={`audit-row-${ev.id}`}
      className="px-5 py-3.5 flex items-start gap-3 hover:bg-slate-50/60"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900">
          <strong>{ev.user_name}</strong>{" "}
          <span className="text-slate-500">({ev.user_role?.replace("_", " ")})</span>{" "}
          <span className="text-slate-600">{meta.label.toLowerCase()}</span>{" "}
          {ev.entity_name && (
            <span className="font-mono text-xs text-slate-700">{ev.entity_name}</span>
          )}
        </div>
        {ev.details?.reason && (
          <div className="text-xs text-amber-700 mt-0.5 italic">"{ev.details.reason}"</div>
        )}
        {ev.details?.reviewer_name && (
          <div className="text-xs text-slate-500 mt-0.5">to {ev.details.reviewer_name}</div>
        )}
        {ev.details?.fields && (
          <div className="text-xs text-slate-500 mt-0.5">
            fields: {ev.details.fields.join(", ")}
          </div>
        )}
      </div>
      <div className="text-[11px] font-mono text-slate-400 shrink-0">
        {when ? when.toLocaleString() : ""}
      </div>
    </li>
  );
}
